"use client";

import {
  FC,
  useState,
  useRef,
  useEffect,
} from "react";
import Link from "next/link";

import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import Container from "apps/website/components/layout/Container/Container";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";

import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import Icon from "../../base/Icon/Icon";

export interface IAnnouncement {
  text: string;
  to?: string;
  image?: IImageProps;
  showTag?: boolean;

}

export interface IAnnouncementBanner {
  announcements: IAnnouncement[];
  theme?: Theme;
  className?: string;
  hideSpacerDiv?: boolean;
  positionClassName?: string;
}

const AnnouncementBanner: FC<IAnnouncementBanner> = ({
  announcements,
  theme = "brand",
  className = "",
  hideSpacerDiv = false,
  positionClassName = "",
}) => {

  const announcementBanner = useRef(null);
  const [ currentAnnouncement, setCurrentAnnouncement ] = useState<number>(0);
  const [ isFading, setIsFading ] = useState<boolean>(false);

  const textComponent = (text: string) => (
    <TextSubtitle
      tag="span"
      size={2}
      align="center"
      className="whitespace-nowrap"
      data-testid="AnnouncementBannerText"
    >
      { text }
    </TextSubtitle>
  );

  useEffect(() => {
    // If the list shrinks, ensure we don't point past the end.
    if (currentAnnouncement > announcements.length - 1) {
      setCurrentAnnouncement(0);
    }
  }, [ announcements.length, currentAnnouncement ]);

  useEffect(() => {
    // If there's only one (or zero) announcements, don't run the carousel animation.
    if (announcements.length <= 1) {
      setIsFading(false);
      return;
    }

    const ROTATE_INTERVAL_MS = 5000;
    const FADE_DURATION_MS = 500;
    // Small delay so React can commit the swapped content before fading in.
    const FADE_IN_DELAY_MS = 20;

    let cancelled = false;
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const wait = (ms: number) => new Promise<void>((resolve) => {
      const id = setTimeout(resolve, ms);
      timers.push(id);
    });

    const run = async () => {
      while (!cancelled) {
        await wait(ROTATE_INTERVAL_MS);
        if (cancelled) {
          return;
        }

        setIsFading(true);
        await wait(FADE_DURATION_MS);
        if (cancelled) {
          return;
        }

        setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
        await wait(FADE_IN_DELAY_MS);
        if (cancelled) {
          return;
        }

        setIsFading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      timers.forEach((id) => clearTimeout(id));
    };
  }, [ announcements.length ]);

  return <>
    <div
      data-component="AnnouncementBanner"
      className={`z-[45] w-full fixed h-[44px] flex justify-center items-center transition-all duration-500 box-border overflow-hidden ${themeRootClassMap[theme]} ${className} ${positionClassName}`}
      data-theme={theme}
      data-state="open"
      ref={announcementBanner}
    // style={{ "--banner-height": announcementBannerHeight } as React.CSSProperties}
    >
      <div>
        <Container>
          <Grid>
            <Column align="center" justify="center">
              { (() => {
                const announcement = announcements[currentAnnouncement];
                if (!announcement) {
                  return null;
                }

                const isVisible = !isFading;
                const commonInner = (
                  <>
                    { announcement.showTag && (
                      <Icon icon="filledTag" size="xsmall" className="mr-1" />
                    ) }
                    { textComponent(announcement.text) }
                    { announcement.image?.src && (
                      <div className="pl-2 w-[100px] hidden lg:block">
                        <Image image={announcement.image} alt={announcement.text} />
                      </div>
                    ) }
                  </>
                );

                return (
                  <div
                    className={`flex items-center justify-center transition-opacity duration-500 ${isFading ? "opacity-0" : "opacity-100"}`}
                    aria-hidden={isVisible ? "false" : "true"}
                  >
                    { announcement.to ? (
                      <Link
                        href={announcement.to}
                        tabIndex={isVisible ? 0 : -1}
                        className="flex items-center justify-center"
                      >
                        { commonInner }
                      </Link>
                    ) : commonInner }
                  </div>
                );
              })() }
            </Column>
          </Grid>
        </Container>
      </div>
    </div>
    { !hideSpacerDiv && <div className={`h-[44px] w-full ${className}`} /> }
  </>;
};

export default AnnouncementBanner;
